/**
 * Ποιο bundle τρέχει σε κάθε ταμείο (κεφαλίδα X-Client-Build, απόφαση 25).
 *
 * ΓΙΑΤΙ: τα ανοιχτά ταμεία κρατούν το παλιό bundle μέχρι να πατηθεί F5. Πριν
 * αλλάξει ο τρόπος που διαβάζονται οι τιμές (κατάργηση base_price) πρέπει να
 * ξέρουμε ότι κανένα ταμείο δεν τρέχει ακόμη κώδικα που δεν καταλαβαίνει το νέο
 * μοντέλο. Ο server καταγράφει το build ανά κατάστημα (client_build_seen) και η
 * πύλη 72 ωρών μετρά ως «παλιό» ό,τι δεν στέλνει κεφαλίδα ή στέλνει 'dev'/'unknown'.
 *
 * Πάντα ενεργό, χωρίς διακόπτη (απόφαση 25): είναι υποδομή παρακολούθησης, όχι
 * λειτουργία του καταστήματος, και δεν αλλάζει κανένα ποσό ή συμπεριφορά.
 *
 * Το sha μπαίνει στο REACT_APP_BUILD_SHA από το craco.config.js τη στιγμή του build.
 */
import { API_BASE_URL } from '../components/config/api';

// Συμβόλαιο με τον server: ^[0-9A-Za-z._+-]{1,64}$. Το git sha (12 hex) το
// τηρεί πάντα· το όριο μήκους μένει για χειροκίνητο GIT_SHA.
export const CLIENT_BUILD: string = (process.env.REACT_APP_BUILD_SHA || 'dev').slice(0, 64);

export const CLIENT_BUILD_HEADER = 'X-Client-Build';

const requestUrl = (input: RequestInfo | URL): string =>
  typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

/**
 * Πάει το αίτημα στο δικό μας API; Μόνο εκεί στέλνουμε την κεφαλίδα: σε τρίτα
 * origins (Google Maps, geocoding) μια άγνωστη κεφαλίδα προκαλεί preflight
 * CORS που εκείνοι μπορεί να απορρίψουν — και το αίτημα θα αποτύγχανε.
 */
export function isApiRequest(url: string): boolean {
  try {
    const target = new URL(url, window.location.origin);
    const api = new URL(API_BASE_URL, window.location.origin);
    if (target.origin === api.origin) return true;
    // Relative '/api/…' (π.χ. πίσω από proxy στο ίδιο origin με τη σελίδα).
    return target.origin === window.location.origin && target.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

/** Για τα request interceptors των axios instances (api, adminApi, supportApi). */
export function addClientBuildHeader<T extends { headers?: any }>(config: T): T {
  if (config.headers) config.headers[CLIENT_BUILD_HEADER] = CLIENT_BUILD;
  return config;
}

/**
 * Τυλίγει το window.fetch ΜΙΑ φορά: η εφαρμογή κάνει εκατοντάδες απλές κλήσεις
 * fetch (βλ. utils/migration.ts), άρα interceptor μόνο στο axios θα άφηνε τα μισά
 * αιτήματα χωρίς build. Αγγίζει μόνο τις κεφαλίδες· σώμα, method, keepalive
 * περνούν αυτούσια. Υπάρχουσα X-Client-Build δεν πατιέται.
 */
export function installFetchClientBuildHeader() {
  const w = window as any;
  if (w.__xentrixBuildHeader) return;
  w.__xentrixBuildHeader = true;
  const original = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    let url: string;
    try { url = requestUrl(input); } catch { return original(input, init); }
    if (!isApiRequest(url)) return original(input, init);
    let headers: Headers;
    try {
      // Κεφαλίδες του init αν δόθηκαν (αυτές θα ίσχυαν ούτως ή άλλως), αλλιώς
      // του Request — αλλιώς το init μας θα έσβηνε π.χ. το Authorization του.
      headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
      if (!headers.has(CLIENT_BUILD_HEADER)) headers.set(CLIENT_BUILD_HEADER, CLIENT_BUILD);
    } catch {
      // Η παρακολούθηση δεν σπάει ποτέ ένα αίτημα: χωρίς κεφαλίδα, όπως πριν.
      return original(input, init);
    }
    return original(input, { ...init, headers });
  };
}
